// @ts-nocheck
import { Dialog, Grid } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { CancelButtonBlack } from '@tassat/button';
import {
    FailedTransactionWorkflowCodes,
    TransactionStatusCode,
    TransactionTypeChar,
    TransactionTypeString,
    transactionTypeCharMap,
    transactionTypeMap
} from '@tassat/common-definitions';
import React from 'react';

import SendConfirmation from 'Containers/client/forms/sendConfirmation';
import TransactConfirmation from 'Containers/client/forms/transactConfirmation';
import WithdrawalConfirmation from 'Containers/client/forms/withdrawalConfirmation';
import history from 'Lib/browserHistory';
import { registerTimeout } from 'Lib/serverTimeout';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppDispatch, useAppSelector } from 'Reducers/store/hooks';
import { getTransactionAccount } from 'Selectors/accounts';
import { getWithdrawalDataById } from 'Selectors/accountWithdrawalData';
import { getAllAssetsArrayWithPrice, getAssetFromTicker, getSecurityCurrencyPairBySecurityTicker } from 'Selectors/assets';
import { getCurrentRole, getUser } from 'Selectors/common';
import { getFirmByFirmId } from 'Selectors/firms';
import { getKillSwitchOn } from 'Selectors/killswitch';
import { getNetAssetValuesAsCurCode } from 'Selectors/netAssetValues';
import { getTransactionById } from 'Selectors/transactions';
import { getUserAccountByTransaction } from 'Selectors/userAccounts';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import TransactionDetails from 'SharedComponents/transactionDetails';
import { ApproveFailedTransaction, DeclineTransaction } from 'SharedComponents/transactions/modals';

import { TransactionModal } from './transactionModal';

export interface TransactionDetailModalProps extends WithStyles<typeof muiStyles> {
    selectedTransactionId: string;
    open: boolean;
    onClose: () => void;
    isLookup: boolean;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: {
        minWidth: '560px',
        padding: '1.5rem'
    },
    detailsBorder: {
        borderTop: `5px solid ${theme?.app.clientTheme.colors.primaryColor ||
        '#375073'}`
    },
    approvalBorder: {
        borderTop: `12px solid ${theme?.app?.applicationColors.successDark ||
        '#5AAC01'}`
    },
    declineBorder: {
        borderTop: `12px solid ${theme?.app?.applicationColors.failureDark ||
        '#AD3D4B'}`
    },
    approvalHeader: {
        alignSelf: 'center',
        fontSize: '2rem',
        fontWeight: 'bold' as const,
        color: theme?.app?.applicationColors.successDark ||
            '#5AAC01'
    },
    declineHeader: {
        alignSelf: 'center',
        fontSize: '2rem',
        fontWeight: 'bold' as const,
        color: theme?.app?.applicationColors.failureDark ||
            '#AD3D4B'
    },
    cancelIcon: {
        position: 'absolute' as const,
        top: 18,
        right: 18
    },
    paperWidthXl: { width: '80%' },
    paperWidthM: { width: '40%' },
    '@media (max-width: 1360px)': {
        paperWidthXl: { width: '700px' },
        paperWidthM: { width: '400px' }
    }
});

export type ModalScreen = 'details' | 'approve' | 'decline' | 'cancel';
export type ModalAction = 'accept' | 'retry' | 'revert' | 'approve' | 'decline' | 'cancel';

export function TransactionDetailsModal(props: TransactionDetailModalProps) {
    const {
        classes,
        selectedTransactionId,
        open,
        onClose,
        isLookup
    } = props;

    const dispatch = useAppDispatch();
    const selectedTransaction = useAppSelector((state) => getTransactionById(state, selectedTransactionId));
    const allAssets = useAppSelector((state) => getAllAssetsArrayWithPrice(state));
    const transactionFirm = useAppSelector((state) => {
        const account = getTransactionAccount(state, selectedTransactionId);
        const firm = getFirmByFirmId(state, account?.FirmId);

        return firm;
    });
    const currentRole = useAppSelector(getCurrentRole);
    const userAccount = useAppSelector((state) => getUserAccountByTransaction(state, selectedTransaction));
    const resetTransactionValidation = () => dispatch({ type: 'RESET_VALIDATION' });
    const onOrderTimeoutError = () => dispatch({ type: 'ORDER_TIMEOUT' });
    const onSendTimeoutError = () => dispatch({ type: 'SEND_TIMEOUT' });
    const onWithdrawalTimeoutError = () => dispatch({ type: 'WITHDRAWAL_TIMEOUT' });

    const killSwitch = useAppSelector(getKillSwitchOn);
    const currentUser = useAppSelector(getUser);
    const navPriceList = useAppSelector(getNetAssetValuesAsCurCode);
    const transactionAccount = useAppSelector((state) => getTransactionAccount(state, selectedTransactionId));
    const assetPair = useAppSelector((state) => getSecurityCurrencyPairBySecurityTicker(
        state, selectedTransaction?.Ticker
    ));
    const asset = useAppSelector((state) => selectedTransaction && getAssetFromTicker(state, selectedTransaction.Ticker));
    const withdrawalData = useAppSelector((state) => {
        return selectedTransaction?.AccountWithdrawalDataId && getWithdrawalDataById(state, selectedTransaction.AccountWithdrawalDataId);
    });
    const [features] = useFeatures();
    const [ modalScreen, setModalScreen ] = React.useState<ModalScreen>('details');
    const [ modalAction, setModalAction ] = React.useState<null | ModalAction>(null);
    const [ transactionInProgress, setTransactionInProgress ] = React.useState<boolean>(false);
    const borderClass = modalScreen === 'details' ? classes.detailsBorder
        : modalScreen === 'approve' ? classes.approvalBorder
            : (modalScreen === 'decline' || modalScreen === 'cancel') ? classes.declineBorder : '';
    const changeModalScreen = (
        screen: ModalScreen,
        // null will be for legacy approval/decline
        action?: null | ModalAction
    ) => {
        if (action) {
            setModalAction(action);
        }
        setModalScreen(screen);
    };

    const navPrice = React.useMemo(() => {
        if (!navPriceList || !assetPair.security?.AssetId) return;
        const navPriceByAssetId = Object.keys(navPriceList)
            .map(key => {
                if (navPriceList[key]?.AssetId === assetPair.security?.AssetId) {
                    return navPriceList[key];
                }
            });

        return navPriceByAssetId.length ? navPriceByAssetId[0] : {};
    }, [ navPriceList, assetPair.security?.AssetId ]);

    React.useEffect(() => {
        if (!selectedTransaction && !isLookup) {
            onClose();

            return;
        }
    }, [ selectedTransaction, isLookup, onClose ]);

    const fetchWithdrawalDetails = async () => {
        try {
            await Worker.getInstance()
                .getTransactionDetail({ RequestId: selectedTransaction.RequestId });
        } catch (error) {
            console.error(error);
        }
    };

    React.useEffect(() => {
        if (!selectedTransaction || selectedTransaction.type !== TransactionTypeChar.withdrawal) return;
        if (selectedTransaction.WithdrawalDetails || withdrawalData) return;

        fetchWithdrawalDetails();
    }, [selectedTransactionId]);

    if (!selectedTransactionId || !selectedTransaction) return <div />;

    const approveTransaction = async (requestId) => {
        setTransactionInProgress(true);
        changeModalScreen('details');
        await Worker.getInstance()
            .approveTransaction(requestId, currentUser, selectedTransaction.TransType, transactionAccount.AccountId);
    };

    const declineTransaction = async (requestId: string, reason: string, isCancel: boolean) => {
        let timeoutFunc;
        let tranType = transactionTypeCharMap(selectedTransaction.type);
        if (tranType === TransactionTypeString.transfer) {
            tranType = TransactionTypeString.send;
        }

        switch (tranType) {
            case TransactionTypeString.send:
                timeoutFunc = onSendTimeoutError;
                break;
            case TransactionTypeString.order:
                timeoutFunc = onOrderTimeoutError;
                break;
            case TransactionTypeString.withdrawal:
                timeoutFunc = onWithdrawalTimeoutError;
                break;
            default:
                timeoutFunc = null;
                break;
        }

        if (!timeoutFunc) return;

        setTransactionInProgress(true);
        changeModalScreen('details');
        registerTimeout(timeoutFunc, tranType.toLowerCase());
        if (isCancel && tranType === TransactionTypeString.order) {
            await Worker.getInstance()
                .cancelTransactionOrder({
                    UserId: currentUser.UserId,
                    FirmId: currentUser.FirmId,
                    OrderId: requestId,
                    Reason: reason
                });
        } else {
            if (isCancel) {
                await Worker.getInstance()
                    .cancelTransaction({
                        TransactionRequestId: requestId,
                        CurrentUser: currentUser,
                        TransactionType: selectedTransaction.TransType,
                        AccountId: transactionAccount.AccountId,
                        Reason: reason
                    });
            } else {
                await Worker.getInstance()
                    .rejectTransaction({
                        TransactionRequestId: requestId,
                        CurrentUser: currentUser,
                        TransactionType: selectedTransaction.TransType,
                        AccountId: transactionAccount.AccountId,
                        Reason: reason
                    });
            }
        }
    };

    const handleClose = (): void => {
        changeModalScreen('details');
        onClose();
    };

    const transactionWorkflowCode = (): FailedTransactionWorkflowCodes => {
        if (modalAction) {
            //info: resusing existing codes for failed external sends/receives
            if (modalAction === 'accept') {
                return FailedTransactionWorkflowCodes.APPROVE;
            } else if (modalAction === 'retry') {
                return FailedTransactionWorkflowCodes.RETRY;
            } else if (modalAction === 'revert') {
                return FailedTransactionWorkflowCodes.DECLINE;
            }
        } else if (modalScreen) {
            if (modalScreen === 'approve') {
                return FailedTransactionWorkflowCodes.APPROVE;
            } else if (modalScreen === 'decline' || modalScreen === 'cancel') {
                return FailedTransactionWorkflowCodes.DECLINE;
            }
        }
    };

    const executeFailedTransactionOperation = async (): Promise<void> => {
        changeModalScreen('details');
        await Worker.getInstance()
            .sendExceptionResponse(
                selectedTransaction,
                transactionAccount.FirmId,
                transactionAccount.AccountId,
                transactionWorkflowCode()
            );
    };

    const renderApproval = () => {
        if (!modalAction && selectedTransaction.Status === TransactionStatusCode.BANK_NO_ACK) {
            return (
                <>
                    <span className={classes.approvalHeader}>APPROVE</span>
                    <ApproveFailedTransaction
                        approveFailedTransaction={executeFailedTransactionOperation}
                        cancelApprove={() => changeModalScreen('details')}
                        selectedTransaction={selectedTransaction}
                    />
                </>
            );
        }

        if (transactionTypeMap(selectedTransaction.TransType) === TransactionTypeString.send) {
            return (
                <>
                    <span className={classes.approvalHeader}>APPROVE</span>
                    <SendConfirmation
                        asset={asset}
                        cancelConfirmation={() => changeModalScreen('details')}
                        currentWallet={transactionAccount}
                        handleSend={() => approveTransaction(selectedTransaction.RequestId)}
                        sendValues={selectedTransaction}
                    />
                </>
            );
        }

        if (transactionTypeMap(selectedTransaction.TransType) === TransactionTypeString.order) {
            return (
                <>
                    <span className={classes.approvalHeader}>APPROVE</span>
                    <TransactConfirmation
                        allAssets={allAssets}
                        assetPair={assetPair}
                        cancelConfirmation={() => changeModalScreen('details')}
                        handleTransact={() => approveTransaction(selectedTransaction.LinkTransRequestId)}
                        navPrice={navPrice}
                        transactValues={selectedTransaction}
                    />
                </>
            );
        }

        if (transactionTypeMap(selectedTransaction.TransType) === TransactionTypeString.withdrawal) {
            const withdrawalInstruction = selectedTransaction.WithdrawalDetails || withdrawalData;

            return (
                <>
                    <span className={classes.approvalHeader}>APPROVE</span>
                    <WithdrawalConfirmation
                        asset={asset}
                        cancelConfirmation={() => changeModalScreen('details')}
                        currentWallet={transactionAccount}
                        handleWithdrawal={() => approveTransaction(selectedTransaction.RequestId)}
                        withdrawalInstruction={withdrawalInstruction}
                        withdrawalValues={selectedTransaction}
                    />
                </>
            );
        }
    };

    return (
        <>
            <Dialog
                classes={{ paperWidthXl: classes.paperWidthXl }}
                maxWidth='xl'
                onClose={handleClose}
                open={open}
                transitionDuration={{
                    enter: 0,
                    exit: 0
                }}
            >
                <Grid
                    className={`${classes.root} ${borderClass}`}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.cancelIcon}
                        item
                    >
                        <CancelButtonBlack
                            onClick={handleClose}
                        />
                    </Grid>
                    {modalScreen === 'details' &&
                        <TransactionDetails
                            approveTransaction={() => changeModalScreen('approve', 'approve')}
                            asset={asset}
                            changeModalScreen={changeModalScreen}
                            currency={assetPair.currency}
                            currentRole={currentRole}
                            currentUser={currentUser}
                            declineTransaction={(isCancel: boolean = false) => {
                            changeModalScreen(
                                isCancel ? 'cancel' : 'decline',
                                isCancel ? 'cancel' : 'decline'
                            );
                        }}
                            history={history}
                            killSwitch={killSwitch}
                            selectedTransaction={selectedTransaction}
                            transactionAccount={transactionAccount}
                            transactionFirm={transactionFirm}
                            userAccount={userAccount}
                            withdrawalData={withdrawalData}
                        />}
                    {modalScreen === 'approve' &&
                        <>
                            {renderApproval()}
                        </>}
                    {(modalScreen === 'decline' || modalScreen === 'cancel') &&
                        <>
                            <span className={classes.declineHeader}>{modalScreen.toUpperCase()}</span>
                            <DeclineTransaction
                                cancelDecline={() => changeModalScreen('details')}
                                declineFailedTransaction={executeFailedTransactionOperation}
                                declineTransaction={declineTransaction}
                                isCancel={modalScreen === 'cancel'}
                                selectedTransaction={selectedTransaction}
                            />
                        </>}
                </Grid>
            </Dialog>
            <TransactionModal
                actionType={modalAction}
                allAssets={allAssets}
                asset={asset}
                onClose={() => {
                    resetTransactionValidation();
                    setTransactionInProgress(false);
                }}
                open={transactionInProgress}
                transactionValues={selectedTransaction}
            />
        </>
    );
}

export default withStyles(muiStyles)(TransactionDetailsModal);